import '../../scripts/common.js';
import {Sentry} from '../_common/my_sentry.js';

class Api {
    user_id;
    username;
    first_name;
    last_name;
    school;
    refreshTokenTimeout;
    prefix;
    authPromise;
    access_token;

    constructor() {
        this.prefix = BACKEND_URL + '/api';
        this.authPromise = this.refreshToken();
        this.authPromise.catch((e) => this.redirectToLogin());
    }

    async refreshToken() {
        const response = await fetch(`${this.prefix}/refresh_token`, {
            credentials: 'include',
            method: 'POST',
        });
        let needLogin = (response.status >= 300 || response.redirected);
        if (!needLogin) {
            try {
                const data = await response.json();
                this.user_id = data.user_id;
                this.username = data.username;
                this.first_name = data.first_name;
                this.last_name = data.last_name;
                this.school = data.school;
                this.access_token = getCookieValue('access_token');
                Sentry.setUser({id: this.user_id, username: this.username});
            } catch (e) {
                console.error(e);
                needLogin = true;
            }
        }
        if (needLogin) {
            this.redirectToLogin();
        }
        this.refreshTokenTimeout = setTimeout(() => this.refreshToken(), 60 * 60 * 1000);
        this.setHeaderName();
        this.applyOfficeRestrictions();
        this.applyWrtCheckRouteRestrictions();
        this.applyTlfSupportRouteRestrictions();
        return this.user_id;
    }


    async _template(chatId) {
        return await postJSON(
            `${this.prefix}/getCode`,
            {chatId},
        );
    }


    async create_testlinks(methodOrPayload, datatable, options = {}) {
        const payload = typeof methodOrPayload === 'object' && methodOrPayload !== null
            ? methodOrPayload
            : {method: methodOrPayload, datatable, ...options};
        return await postJSON(
            `${this.prefix}/create_testlinks`,
            payload,
        );
    }

    async getEnglishReviewCandidates(exam_template_id = null) {
        return await postJSON(`${this.prefix}/admin/english/candidates`, {exam_template_id});
    }

    async getEnglishReview(test_link_id) {
        return await postJSON(`${this.prefix}/admin/english/review`, {test_link_id});
    }

    async saveEnglishBinaryReview(part_id, score, revision) {
        return await postJSON(`${this.prefix}/admin/english/review/binary`, {part_id, score, revision});
    }

    async saveEnglishWritingReview(payload) {
        return await postJSON(`${this.prefix}/admin/english/review/writing`, payload);
    }

    async saveEnglishOralReview(payload) {
        return await postJSON(`${this.prefix}/admin/english/review/oral`, payload);
    }

    async create_decisions(method, datatable) {
        return await postJSON(
            `${this.prefix}/create_decisions`,
            {method, datatable},
        );
    }

    async get_decision_students() {
        return await postJSON(
            `${this.prefix}/get_decision_students`,
            {},
        );
    }

    async get_decision_labels() {
        return await postJSON(
            `${this.prefix}/get_decision_labels`,
            {},
        );
    }

    async get_decision_types() {
        return await postJSON(
            `${this.prefix}/get_decision_types`,
            {},
        );
    }

    async get_decision_type(id) {
        return await postJSON(
            `${this.prefix}/get_decision_type`,
            {id},
        );
    }

    async create_decision_type(item) {
        return await postJSON(
            `${this.prefix}/create_decision_type`,
            {item},
        );
    }

    async update_decision_type(id, item) {
        return await postJSON(
            `${this.prefix}/update_decision_type`,
            {id, item},
        );
    }

    async get_student_decision_timeline(student_email) {
        return await postJSON(
            `${this.prefix}/get_student_decision_timeline`,
            {student_email},
        );
    }

    async update_decision_comment(id, comment) {
        return await postJSON(
            `${this.prefix}/update_decision_comment`,
            {id, comment},
        );
    }

    async delete_decision(id) {
        return await postJSON(
            `${this.prefix}/delete_decision`,
            {id},
        );
    }

    async get_all_written_imgs(exam_template_ids) {
        return await postJSON(
            `${this.prefix}/get_all_written_imgs`,
            {exam_template_ids},
        );
    }

    async get_written_reassignment_tasks(test_link_id, exam_template_id) {
        return await postJSON(
            `${this.prefix}/get_written_reassignment_tasks`,
            {test_link_id, exam_template_id},
        );
    }

    async reassign_written_solution(written_solution_id, target_test_instance_task_id, client_id = null) {
        return await postJSON(
            `${this.prefix}/reassign_written_solution`,
            {written_solution_id, target_test_instance_task_id, client_id},
        );
    }

    async add_or_replace_comment(test_link_id, test_instance_task_id, written_solution_id, comment, was_cheating = null, client_id = null) {
        const payload = {test_link_id, test_instance_task_id, written_solution_id};
        if (comment !== undefined) {
            payload.comment = comment;
        }
        if (was_cheating !== undefined && was_cheating !== null) {
            payload.was_cheating = was_cheating;
        }
        if (client_id !== undefined && client_id !== null) {
            payload.client_id = client_id;
        }
        return await postJSON(
            `${this.prefix}/add_or_replace_comment`,
            payload,
        );
    }

    async add_or_replace_verdict(test_link_id, test_instance_task_id, task_id, verdict, clear = false, client_id = null) {
        return await postJSON(
            `${this.prefix}/add_or_replace_verdict`,
            {test_link_id, test_instance_task_id, task_id, verdict, clear, client_id},
        );
    }

    async add_or_replace_written_annotation(
        test_link_id,
        test_instance_task_id,
        written_solution_id,
        annotation_objects,
        rotation_degrees = 0,
        zoom = 1,
        pan_x = 0,
        pan_y = 0,
        client_id = null
    ) {
        return await postJSON(
            `${this.prefix}/add_or_replace_written_annotation`,
            {test_link_id, test_instance_task_id, written_solution_id, annotation_objects, rotation_degrees, zoom, pan_x, pan_y, client_id},
        );
    }

    async get_active_exam_templates() {
        return await postJSON(
            `${this.prefix}/get_active_exam_templates`,
            {},
        );
    }

    async get_all_testlinks(method, datatable) {
        return await postJSON(
            `${this.prefix}/get_all_testlinks`,
            {method, datatable},
        );
    }

    async get_active_tests() {
        return await postJSON(
            `${this.prefix}/get_active_tests`,
            {},
        );
    }

    async get_oral_review_candidates() {
        return await postJSON(
            `${this.prefix}/get_oral_review_candidates`,
            {},
        );
    }

    async get_oral_review(test_link_id) {
        return await postJSON(
            `${this.prefix}/get_oral_review`,
            {test_link_id},
        );
    }

    async upsert_oral_review(payload) {
        return await postJSON(
            `${this.prefix}/upsert_oral_review`,
            payload,
        );
    }

    async get_test_link_status_by_token(token) {
        return await postJSON(
            `${this.prefix}/get_test_link_status_by_token`,
            {token},
        );
    }

    async get_constructor_archive_sessions({
        limit = 20,
        offset = 0,
        search = '',
        examType = '',
        conclusion = '',
        dateFrom = '',
        dateTo = '',
    } = {}) {
        return await postJSON(
            `${this.prefix}/get_constructor_archive_sessions`,
            {limit, offset, search, examType, conclusion, dateFrom, dateTo},
        );
    }

    async get_constructor_archive_link(sessionId, returnUrl = null) {
        return await postJSON(
            `${this.prefix}/get_constructor_archive_link`,
            {sessionId, returnUrl},
        );
    }

    async get_problems_to_solve_by_token(token) {
        return await postJSON(
            `${this.prefix}/get_problems_to_solve_by_token`,
            {token},
        );
    }

    async get_applications2027(filters = {}, limit = 100, offset = 0, orderBy = 'created_at desc') {
        return await postJSON(
            `${this.prefix}/get_applications2027`,
            {filters, limit, offset, order_by: orderBy},
        );
    }

    async get_application2027(id) {
        return await postJSON(
            `${this.prefix}/get_application2027`,
            {id},
        );
    }

    async update_application2027(id, patch, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/update_application2027`,
            {id, patch, source},
        );
    }

    async get_application2027_audit(id) {
        return await postJSON(
            `${this.prefix}/get_application2027_audit`,
            {id},
        );
    }

    async get_support_contacts(filters = {}) {
        return await postJSON(
            `${this.prefix}/support_contacts`,
            filters,
        );
    }

    async get_support_contact_detail(contact_id) {
        return await postJSON(
            `${this.prefix}/support_contact_detail`,
            {contact_id},
        );
    }

    async claim_support_contact(contact_id) {
        return await postJSON(
            `${this.prefix}/support_contact_claim`,
            {contact_id},
        );
    }

    async release_support_contact(contact_id) {
        return await postJSON(
            `${this.prefix}/support_contact_release`,
            {contact_id},
        );
    }

    async mark_support_message_no_reply(message_id) {
        return await postJSON(
            `${this.prefix}/support_message_no_reply`,
            {message_id},
        );
    }

    async send_support_reply(source_message_id, text) {
        return await postJSON(
            `${this.prefix}/support_reply`,
            {source_message_id, text},
        );
    }

    async retry_support_message(message_id) {
        return await postJSON(
            `${this.prefix}/support_retry_message`,
            {message_id},
        );
    }

    async search_support_link_candidates(search, limit = 10) {
        return await postJSON(
            `${this.prefix}/support_link_candidates`,
            {search, limit},
        );
    }

    async link_support_contact(contact_id, application_id) {
        return await postJSON(
            `${this.prefix}/support_link_contact`,
            {contact_id, application_id},
        );
    }

    async get_support_attachment_url(attachment_id) {
        return await postJSON(
            `${this.prefix}/support_attachment_url`,
            {attachment_id},
        );
    }

    async get_exam_templates() {
        return await postJSON(
            `${this.prefix}/get_exam_templates`,
            {},
        );
    }

    async get_exam_template(id) {
        return await postJSON(
            `${this.prefix}/get_exam_template`,
            {id},
        );
    }

    async get_exam_types() {
        return await postJSON(
            `${this.prefix}/get_exam_types`,
            {},
        );
    }

    async get_exam_template_audit(id) {
        return await postJSON(
            `${this.prefix}/get_exam_template_audit`,
            {id},
        );
    }

    async get_email_templates() {
        return await postJSON(
            `${this.prefix}/get_email_templates`,
            {},
        );
    }

    async get_email_template(id) {
        return await postJSON(
            `${this.prefix}/get_email_template`,
            {id},
        );
    }

    async create_email_template(item, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/create_email_template`,
            {item, source},
        );
    }

    async update_email_template(id, item, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/update_email_template`,
            {id, item, source},
        );
    }

    async get_email_template_audit(id) {
        return await postJSON(
            `${this.prefix}/get_email_template_audit`,
            {id},
        );
    }

    async get_mailing_template_choices() {
        return await postJSON(
            `${this.prefix}/get_mailing_template_choices`,
            {},
        );
    }

    async check_mailing(email_template_id, datatable, options = {}) {
        return await postJSON(
            `${this.prefix}/check_mailing`,
            {email_template_id, datatable, ...options},
        );
    }

    async start_mailing(email_template_id, datatable, check_token, options = {}) {
        return await postJSON(
            `${this.prefix}/start_mailing`,
            {email_template_id, datatable, check_token, ...options},
        );
    }

    async get_emails_sent_statuses(ids) {
        return await postJSON(
            `${this.prefix}/get_emails_sent_statuses`,
            {ids},
        );
    }

    async get_emails_sent(filters = {}) {
        return await postJSON(
            `${this.prefix}/get_emails_sent`,
            filters,
        );
    }

    async get_email_sent(id) {
        return await postJSON(
            `${this.prefix}/get_email_sent`,
            {id},
        );
    }

    async get_sync_tasks() {
        return await postJSON(
            `${this.prefix}/get_sync_tasks`,
            {},
        );
    }

    async get_sync_task(task_id) {
        return await postJSON(
            `${this.prefix}/get_sync_task`,
            {task_id},
        );
    }

    async create_sync_task(item, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/create_sync_task`,
            {item, source},
        );
    }

    async update_sync_task(item, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/update_sync_task`,
            {item, source},
        );
    }

    async delete_sync_task(task_id, confirm = true, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/delete_sync_task`,
            {task_id, confirm, source},
        );
    }

    async get_sync_tasks_schema() {
        return await postJSON(
            `${this.prefix}/get_sync_tasks_schema`,
            {},
        );
    }

    async get_task_links(exam_template_ids = null, search = '', limit = 200, offset = 0) {
        return await postJSON(
            `${this.prefix}/get_task_links`,
            {exam_template_ids, search, limit, offset},
        );
    }

    async get_task_links_dictionaries() {
        return await postJSON(
            `${this.prefix}/get_task_links_dictionaries`,
            {},
        );
    }

    async oral_written_solutions_filters() {
        return await postJSON(
            `${this.prefix}/oral_written_solutions_filters`,
            {},
        );
    }

    async oral_written_solution_links(exam_template_ids, search = '', limit = 500, offset = 0) {
        return await postJSON(
            `${this.prefix}/oral_written_solution_links`,
            {exam_template_ids, search, limit, offset},
        );
    }

    async oral_written_solution_context(test_link_id) {
        return await postJSON(
            `${this.prefix}/oral_written_solution_context`,
            {test_link_id},
        );
    }

    async update_task_link(item, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/update_task_link`,
            {item, source},
        );
    }

    async delete_task_link(id, confirm = true, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/delete_task_link`,
            {id, confirm, source},
        );
    }

    async get_personal_task_links(exam_template_ids = null, search = '', status_group = 'all', date_bucket = 'all', limit = 200, offset = 0) {
        return await postJSON(
            `${this.prefix}/get_personal_task_links`,
            {exam_template_ids, search, status_group, date_bucket, limit, offset},
        );
    }

    async get_personal_task_links_dictionaries() {
        return await postJSON(
            `${this.prefix}/get_personal_task_links_dictionaries`,
            {},
        );
    }

    async get_personal_test_link_tasks(test_link_id) {
        return await postJSON(
            `${this.prefix}/get_personal_test_link_tasks`,
            {test_link_id},
        );
    }

    async update_personal_test_link_tasks(test_link_id, items, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/update_personal_test_link_tasks`,
            {test_link_id, items, source},
        );
    }

    async get_timeslots_admin() {
        return await postJSON(
            `${this.prefix}/get_timeslots_admin`,
            {},
        );
    }

    async get_selected_timeslots_admin(search = '') {
        return await postJSON(
            `${this.prefix}/get_selected_timeslots_admin`,
            {search},
        );
    }

    async get_timeslot_admin(id) {
        return await postJSON(
            `${this.prefix}/get_timeslot_admin`,
            {id},
        );
    }

    async update_timeslot_admin(item, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/update_timeslot_admin`,
            {item, source},
        );
    }

    async cancel_selected_timeslot_admin(id, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/cancel_selected_timeslot_admin`,
            {id, source},
        );
    }

    async move_selected_timeslot_admin(id, target_slot_id, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/move_selected_timeslot_admin`,
            {id, target_slot_id, source},
        );
    }

    async delete_timeslot_admin(id, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/delete_timeslot_admin`,
            {id, source},
        );
    }

    async get_timeslot_program_settings(program) {
        return await postJSON(
            `${this.prefix}/get_timeslot_program_settings`,
            {program},
        );
    }

    async update_timeslot_program_settings(program, item, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/update_timeslot_program_settings`,
            {program, item, source},
        );
    }

    async preview_generated_timeslots(payload) {
        return await postJSON(
            `${this.prefix}/preview_generated_timeslots`,
            payload,
        );
    }

    async create_generated_timeslots(items, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/create_generated_timeslots`,
            {items, source},
        );
    }

    async get_task_link_written_upload_context(token) {
        return await postJSON(
            `${this.prefix}/get_task_link_written_upload_context`,
            {token},
        );
    }

    async get_task_link_written_uploads(token) {
        return await postJSON(
            `${this.prefix}/get_task_link_written_uploads`,
            {token},
        );
    }

    async save_task_link_written_upload_photo(token, test_instance_task_id, formData) {
        return await postBlob(
            `${this.prefix}/save_task_link_written_upload_photo?token=${encodeURIComponent(token)}&test_instance_task_id=${encodeURIComponent(test_instance_task_id)}`,
            formData,
        );
    }

    async init_task_link_written_upload(token, payload) {
        return await postJSON(
            `${this.prefix}/init_task_link_written_upload`,
            {token, ...payload},
        );
    }

    async finalize_task_link_written_upload(token, payload) {
        return await postJSON(
            `${this.prefix}/finalize_task_link_written_upload`,
            {token, ...payload},
        );
    }

    async delete_task_link_written_upload(token, written_solution_id) {
        return await postJSON(
            `${this.prefix}/delete_task_link_written_upload`,
            {token, written_solution_id},
        );
    }

    async get_exam_template_tasks(exam_template_id) {
        return await postJSON(
            `${this.prefix}/get_exam_template_tasks`,
            {exam_template_id},
        );
    }

    async get_tasks() {
        return await postJSON(
            `${this.prefix}/get_tasks`,
            {},
        );
    }

    async get_task_variants_preview() {
        return await postJSON(
            `${this.prefix}/get_task_variants_preview`,
            {},
        );
    }

    async get_task_variants_by_task_id(task_id) {
        return await postJSON(
            `${this.prefix}/get_task_variants_by_task_id`,
            {task_id},
        );
    }

    async get_task(task_id) {
        return await postJSON(
            `${this.prefix}/get_task`,
            {task_id},
        );
    }

    async get_task_variants(task_id) {
        return await postJSON(
            `${this.prefix}/get_task_variants`,
            {task_id},
        );
    }

    async create_task(name, title = null, created_at = null) {
        return await postJSON(
            `${this.prefix}/create_task`,
            {name, title, created_at},
        );
    }

    async update_task(task_id, patch) {
        return await postJSON(
            `${this.prefix}/update_task`,
            {task_id, ...patch},
        );
    }

    async create_task_variant(task_id, item, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/create_task_variant`,
            {task_id, item, source},
        );
    }

    async update_task_variant(id, item, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/update_task_variant`,
            {id, item, source},
        );
    }

    async delete_task_variant(id, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/delete_task_variant`,
            {id, source},
        );
    }

    async parse_tasks_from_tex({file = null, tex = ''} = {}) {
        const form = new FormData();
        if (typeof tex === 'string' && tex.trim()) {
            form.append('tex', tex);
        } else if (file) {
            form.append('file', file, file.name || 'tasks.tex');
        } else {
            throw new Error('tex or file is required');
        }
        const response = await fetch(`${this.prefix}/parse_tasks_from_tex`, {
            method: 'POST',
            credentials: 'include',
            body: form,
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async validate_task_answer(answer_type, validation_regex, correct_answer, student_answer = null) {
        return await postJSON(
            `${this.prefix}/validate_task_answer`,
            {answer_type, validation_regex, correct_answer, student_answer},
        );
    }

    async get_latest_mathimg_by_filenames(filenames, kind = 'image') {
        return await postJSON(
            `${this.prefix}/mathimg/get_latest_by_filenames`,
            {filenames, kind},
        );
    }

    async update_exam_template_tasks(exam_template_id, items, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/update_exam_template_tasks`,
            {exam_template_id, items, source},
        );
    }

    async create_exam_template(item, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/create_exam_template`,
            {item, source},
        );
    }

    async update_exam_template(id, item, source = 'admin_ui') {
        return await postJSON(
            `${this.prefix}/update_exam_template`,
            {id, item, source},
        );
    }

    async get_surveys_admin() {
        return await postJSON(`${this.prefix}/admin/surveys/list`, {});
    }

    async get_survey_admin(id) {
        return await postJSON(`${this.prefix}/admin/surveys/get`, {id});
    }

    async create_survey_admin(item, source = 'admin_ui') {
        return await postJSON(`${this.prefix}/admin/surveys/create`, {item, source});
    }

    async update_survey_admin(id, expected_updated_at, item, source = 'admin_ui') {
        return await postJSON(`${this.prefix}/admin/surveys/update`, {id, expected_updated_at, item, source});
    }

    async get_survey_results_admin(id) {
        return await postJSON(`${this.prefix}/admin/surveys/results`, {id});
    }

    async login(login, password, rememberMe) {
        return await postJSON(
            `${this.prefix}/login`,
            {login, password, rememberMe},
        );
    }

    async postCode(chatId, messages, html, css, js, page) {
        const response = await postJSON(
            `${this.prefix}/saveCode`,
            {chatId, messages, html, css, js, page},
        );
        return response.toString();
    }

    redirectToLogin() {
        const currentUrl = encodeURIComponent(window.location.href);
        if (currentUrl.indexOf('login') === -1) {
            window.location.href = `/en/admin/login.html?redirect=${currentUrl}`;
        }
    }

    redirectToMain() {
        window.location.href = `/en/admin/main.html`;
    }

    setHeaderName() {
        const forUserName = document.getElementById('adminUserName');
        if (forUserName) {
            forUserName.innerText = `${this.first_name} ${this.last_name}`;
        }
    }

    isOfficeUser() {
        return this.school === 'TLF-Office';
    }

    isWrtCheckUser() {
        return this.school === 'TLF-WrtCheck';
    }

    isTlfSupportUser() {
        return this.school === 'TLF-Support';
    }

    applyOfficeRestrictions() {
        const isOffice = this.isOfficeUser();
        const isWrtCheck = this.isWrtCheckUser();
        const isTlfSupport = this.isTlfSupportUser();

        const interfaceItems = document.querySelectorAll('[data-admin-interface=\"true\"]');
        if (isWrtCheck && interfaceItems.length) {
            interfaceItems.forEach((el) => {
                const allowedForWrtCheck = el.getAttribute('data-wrtcheck-only') === 'true';
                el.classList.toggle('hidden', !allowedForWrtCheck);
            });
            return;
        }
        if (isTlfSupport && interfaceItems.length) {
            interfaceItems.forEach((el) => {
                const allowedForTlfSupport = el.getAttribute('data-tlf-support-only') === 'true';
                el.classList.toggle('hidden', !allowedForTlfSupport);
            });
            return;
        }

        const officeOnly = document.querySelectorAll('[data-office-only=\"true\"]');
        officeOnly.forEach((el) => {
            el.classList.toggle('hidden', !isOffice);
        });

        const wrtOnly = document.querySelectorAll('[data-wrtcheck-only=\"true\"]');
        wrtOnly.forEach((el) => {
            el.classList.toggle('hidden', !isOffice);
        });

        const tlfSupportOnly = document.querySelectorAll('[data-tlf-support-only=\"true\"]');
        tlfSupportOnly.forEach((el) => {
            el.classList.toggle('hidden', !isOffice);
        });
    }

    requireOfficeAccess() {
        if (this.isOfficeUser()) {
            return true;
        }
        if (this.isTlfSupportUser()) {
            window.location.href = '/en/admin/support.html';
            return false;
        }
        this.redirectToMain();
        return false;
    }

    requireSupportAccess() {
        if (this.isOfficeUser() || this.isTlfSupportUser()) {
            return true;
        }
        this.redirectToMain();
        return false;
    }

    requireDecisionTimelineAccess() {
        if (this.isOfficeUser() || this.isTlfSupportUser()) {
            return true;
        }
        this.redirectToMain();
        return false;
    }

    requireWrittenCheckingAccess() {
        if (this.isOfficeUser() || this.isWrtCheckUser()) {
            return true;
        }
        this.redirectToMain();
        return false;
    }

    applyWrtCheckRouteRestrictions() {
        if (!this.isWrtCheckUser()) {
            return;
        }
        const path = String(window.location.pathname || '').toLowerCase();
        const isAdminPath = path.includes('/admin/') || path.endsWith('/admin');
        if (!isAdminPath) {
            return;
        }
        const allowed = (
            path.endsWith('/main.html')
            || path.endsWith('/viewwrittensols.html')
            || path.endsWith('/oralwrittensols.html')
            || path.endsWith('/viewenglishsols.html')
            || path.endsWith('/login.html')
            || path.endsWith('/admin/')
        );
        if (!allowed) {
            window.location.href = '/en/admin/viewwrittensols.html';
        }
    }

    applyTlfSupportRouteRestrictions() {
        if (!this.isTlfSupportUser()) {
            return;
        }
        const path = String(window.location.pathname || '').toLowerCase();
        const isAdminPath = path.includes('/admin/') || path.endsWith('/admin');
        if (!isAdminPath) {
            return;
        }
        const allowed = (
            path.endsWith('/main.html')
            || path.endsWith('/support.html')
            || path.endsWith('/viewdecisions.html')
            || path.endsWith('/login.html')
            || path.endsWith('/admin/')
            || path.endsWith('/admin')
        );
        if (!allowed) {
            window.location.href = '/en/admin/support.html';
        }
    }
}


async function postJSON(url, data) {
    try {
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            try {
                const payload = await response.json();
                if (payload && typeof payload.msg === 'string' && payload.msg.trim()) {
                    errorMessage = payload.msg.trim();
                }
            } catch (e) {
            }
            throw new Error(errorMessage);
        }

        return await response.json();
    } catch (error) {
        console.error('Error during POST request:', error);
        throw error; // Rethrow to allow caller to handle it
    }
}


async function postBlob(url, formData) {
    try {
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });

        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            try {
                const payload = await response.json();
                if (payload && typeof payload.msg === 'string' && payload.msg.trim()) {
                    errorMessage = payload.msg.trim();
                }
            } catch (e) {
            }
            throw new Error(errorMessage);
        }

        return await response.json();
    } catch (error) {
        console.error('Error during blob POST request:', error);
        throw error;
    }
}


function getCookieValue(name) {
    const regex = new RegExp(`(^| )${name}=([^;]+)`);
    const match = document.cookie.match(regex);
    if (match) {
        return match[2];
    }
}

export const api = new Api();
